// src/components/AppShell.tsx - UPDATED with Context Toolbar and Header Actions
/**
 * T-34: App Shell & Global Toolbar Integration - ENHANCED
 * 
 * UPDATES ADDED:
 * - ContextToolbar integration for tool-specific interfaces
 * - HeaderActions for share/save/load functionality
 * - Proper tool mode context switching
 * - Statistics and feature toggles in header
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { GlobalToolbar } from './GlobalToolbar';
import { ContextToolbar } from './ContextToolbar';
import HeaderActions from './HeaderActions';
import { selectActiveTool } from '../store/slices/toolModeSlice';

interface AppShellProps {
  children: React.ReactNode;
  numberLineRef?: React.RefObject<HTMLDivElement>;
}

export function AppShell({ children, numberLineRef }: AppShellProps) {
  const preferences = useSelector((state: RootState) => state.preferences);
  const activeTool = useSelector(selectActiveTool);
  const viewport = useSelector((state: RootState) => state.viewport);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Start collapsed on mobile

  // Responsive breakpoint detection
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set initial sidebar state based on screen size
  React.useEffect(() => {
    if (windowWidth < 768) {
      // Mobile: start collapsed
      setSidebarCollapsed(true);
    } else {
      // Desktop: respect user preference (default to open)
      const defaultOpen = preferences.sidebarDefaultOpen ?? true;
      setSidebarCollapsed(!defaultOpen);
    }
  }, [windowWidth, preferences.sidebarDefaultOpen]);

  // Auto-collapse sidebar on mobile
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const shouldCollapseSidebar = sidebarCollapsed;
  const isVerticalOrientation = viewport.orientation === 'vertical';

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-200 ${
      preferences.theme === 'dark' 
        ? 'bg-gray-900 text-white' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`flex-shrink-0 shadow-sm border-b transition-colors duration-200 ${
        preferences.theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  className={`p-2 rounded-lg transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  aria-label="Toggle sidebar"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d={shouldCollapseSidebar ? "M4 6h16M4 12h16M4 18h16" : "M6 18L18 6M6 6l12 12"} />
                  </svg>
                </button>
              )}

              {/* Desktop sidebar toggle */}
              {!isMobile && (
                <button
                  onClick={toggleSidebar}
                  className={`p-2 rounded-lg transition-colors ${
                    preferences.theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  title={shouldCollapseSidebar ? "Expand toolbar" : "Collapse toolbar"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d={shouldCollapseSidebar ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                  </svg>
                </button>
              )}

              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  preferences.theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
                }`}>
                  <span className="text-white font-bold text-sm">NL</span>
                </div>
                <div>
                  <h1 className="text-lg font-semibold">Number Line</h1>
                  {activeTool !== 'pan' && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {activeTool === 'jump' && 'Jump Indicators'}
                      {activeTool === 'measurement' && 'Measurement Tool'}
                      {activeTool === 'annotation' && 'Drawing & Annotation'}
                      {activeTool === 'expression' && 'Mathematical Expressions'}
                      {activeTool === 'equation' && 'Graph Equation'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Header Actions */}
            <HeaderActions className="hidden sm:flex" numberLineRef={numberLineRef} />
            
            {/* Mobile header actions (simplified) */}
            <div className="sm:hidden flex items-center gap-2">
              <HeaderActions className="flex" numberLineRef={numberLineRef} />
            </div>
          </div>
        </div>
      </header>

      {/* Context Toolbar - Shows tool-specific interfaces */}
      <ContextToolbar />

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar / Toolbar */}
        <aside className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
          shouldCollapseSidebar 
            ? (isMobile ? 'w-0' : 'w-12') // Slightly narrower when collapsed to preserve vertical layout
            : 'w-64' // Full width when expanded
        } ${
          preferences.theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } border-r overflow-hidden`}>
          {/* Mobile Overlay */}
          {isMobile && !shouldCollapseSidebar && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={toggleSidebar}
            />
          )}
          
          {/* Toolbar Content */}
          <div className={`${
            isMobile && !shouldCollapseSidebar 
              ? 'fixed left-0 top-16 bottom-0 w-64 z-50' 
              : 'relative'
          } ${
            preferences.theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          } h-full`}>
            <GlobalToolbar collapsed={shouldCollapseSidebar} />
          </div>
        </aside>

        {/* Main Content Area */}
        <main className={`flex-1 ${isVerticalOrientation ? 'overflow-hidden' : 'overflow-auto'}`}>
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}